import { useEffect, useState } from "react";
import { useAuth, useUser } from "@clerk/react";
import { Flame, Coins, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { makeApi, coinToast } from "@/lib/api";
import { useT } from "@/i18n";
import { LumiMascot, SparkMascot } from "@/components/Mascots";
import ModulesGrid from "@/components/ModulesGrid";
import Badges from "@/components/Badges";
import CoinRewards from "@/components/CoinRewards";

export default function Dashboard() {
  const { t, lang } = useT();
  const { getToken } = useAuth();
  const { user } = useUser();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const api = makeApi(getToken);
    const loadMe = () => api.get("/me").then((r) => setMe(r.data)).catch(() => {});
    loadMe();
    api.get("/parent/overview").then((r) => setStats(r.data.stats)).catch(() => {});
    window.addEventListener("coins-changed", loadMe);
    return () => window.removeEventListener("coins-changed", loadMe);
  }, [getToken]);

  useEffect(() => {
    makeApi(getToken).post("/rewards/daily").then((r) => coinToast(r.data.awarded, lang)).catch(() => {});
  }, [getToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const coins = me?.profile?.coins ?? 0;
  const streak = me?.profile?.streak ?? 0;
  const firstName = user?.firstName || user?.fullName || "Explorer";

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {/* Greeting */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-fredoka text-4xl sm:text-5xl font-bold text-slate-900">
              {t.dashboard.hello}, <span className="text-sky-600">{firstName}!</span>
            </h1>
            <p className="text-slate-700 mt-2">
              {lang === "es"
                ? "¿Qué quieres aprender hoy?"
                : "What do you want to learn today?"}
            </p>
          </div>
          <div className="flex gap-3">
            <div className="bg-orange-200 border-2 border-slate-900 chunky-shadow-sm rounded-2xl px-4 py-2 flex items-center gap-2" data-testid="stat-streak">
              <Flame className="w-5 h-5 text-orange-600" />
              <span className="font-bold text-slate-900">{streak}</span>
              <span className="text-xs text-slate-700">{t.dashboard.streak}</span>
            </div>
            <div className="bg-yellow-200 border-2 border-slate-900 chunky-shadow-sm rounded-2xl px-4 py-2 flex items-center gap-2" data-testid="stat-coins">
              <Coins className="w-5 h-5 text-yellow-700" />
              <span className="font-bold text-slate-900">{coins}</span>
              <span className="text-xs text-slate-700">{t.dashboard.coins}</span>
            </div>
          </div>
        </div>

        {/* Today's mission + chat cards */}
        <div className="mt-10 grid lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-yellow-200 border-4 border-slate-900 chunky-shadow rounded-3xl p-6 relative overflow-hidden"
          >
            <div className="flex items-center gap-4">
              <LumiMascot size={90} />
              <div className="pl-2">
                <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  {t.dashboard.todaysMission}
                </p>
                <h3 className="font-fredoka text-2xl font-bold text-slate-900 mt-1">
                  {lang === "es"
                    ? "Pide 3 ideas de negocio a Lumi"
                    : "Ask Lumi for 3 business ideas"}
                </h3>
                <Link
                  to="/chat/lumi"
                  className="press-btn mt-4 inline-flex items-center gap-2 bg-slate-900 text-yellow-300 rounded-full px-5 py-2 font-bold border-2 border-slate-900"
                  data-testid="todays-mission-btn"
                >
                  <MessageCircle className="w-4 h-4" /> {t.dashboard.startChat} Lumi
                </Link>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-sky-200 border-4 border-slate-900 chunky-shadow rounded-3xl p-6 relative overflow-hidden"
          >
            <div className="flex items-center gap-4">
              <SparkMascot size={90} />
              <div className="pl-2">
                <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  {lang === "es" ? "Pregunta a Spark" : "Ask Spark"}
                </p>
                <h3 className="font-fredoka text-2xl font-bold text-slate-900 mt-1">
                  {lang === "es"
                    ? "¿Qué es la inteligencia artificial?"
                    : "What is artificial intelligence?"}
                </h3>
                <Link
                  to="/chat/spark"
                  className="press-btn mt-4 inline-flex items-center gap-2 bg-slate-900 text-sky-300 rounded-full px-5 py-2 font-bold border-2 border-slate-900"
                  data-testid="chat-spark-btn"
                >
                  <MessageCircle className="w-4 h-4" /> {t.dashboard.startChat} Spark
                </Link>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-10"><CoinRewards /></div>
        <div className="mt-8"><Badges stats={stats} /></div>

        {/* Modules */}
        <div className="mt-12">
          <h2 className="font-fredoka text-3xl font-bold text-slate-900">
            {t.modulesTitle}
          </h2>
          <div className="mt-6">
            <ModulesGrid />
          </div>
        </div>
      </div>
    </div>
  );
}
